import { useState, useEffect } from 'react'
import { X, Eye, EyeOff, Save } from 'lucide-react'
import { useApp } from '../../store/AppContext'

export interface SensorConfig {
    key: string
    label: string
    category: 'water' | 'system' | 'location' | 'imu' | 'other'
    visible: boolean
}

const DEFAULT_SENSORS: SensorConfig[] = [
    // Water Quality
    { key: 'ph', label: 'pH', category: 'water', visible: true },
    { key: 'nh4', label: 'NH4+', category: 'water', visible: true },
    { key: 'o2', label: 'Dissolved Oxygen', category: 'water', visible: true },
    { key: 'clarity', label: 'Clarity', category: 'water', visible: true },
    { key: 'turbidity', label: 'Turbidity', category: 'water', visible: true },
    { key: 'wt', label: 'Water Temp', category: 'water', visible: true },

    // Temperature Array
    { key: 't1', label: 'Temp 1', category: 'water', visible: false },
    { key: 't2', label: 'Temp 2', category: 'water', visible: false },
    { key: 't3', label: 'Temp 3', category: 'water', visible: false },
    { key: 't4', label: 'Temp 4', category: 'water', visible: false },
    { key: 't5', label: 'Temp 5', category: 'water', visible: false },

    // System
    { key: 'bat', label: 'Phone Battery', category: 'system', visible: true },
    { key: 'vbat', label: 'Battery Voltage', category: 'system', visible: false },
    { key: 'soc', label: 'State of Charge', category: 'system', visible: false },
    { key: 'wifiDbm', label: 'WiFi Signal', category: 'system', visible: false },
    { key: 'cellDbm', label: 'Cell Signal', category: 'system', visible: false },

    // Location & IMU
    { key: 'lat', label: 'Latitude', category: 'location', visible: true },
    { key: 'lon', label: 'Longitude', category: 'location', visible: true },
    { key: 'altMsl', label: 'Altitude', category: 'location', visible: false },
    { key: 'hea', label: 'Heading', category: 'imu', visible: false },
    { key: 'pit', label: 'Pitch', category: 'imu', visible: false },
    { key: 'roll', label: 'Roll', category: 'imu', visible: false },
]

interface Props {
    isOpen: boolean
    onClose: () => void
    onSave: (configs: SensorConfig[]) => void
}

export default function SensorSettings({ isOpen, onClose, onSave }: Props) {
    const { t } = useApp()
    const [configs, setConfigs] = useState<SensorConfig[]>(DEFAULT_SENSORS)

    useEffect(() => {
        const saved = localStorage.getItem('sensor_configs')
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                if (Array.isArray(parsed)) {
                    // Merge with default to ensure new sensors appear
                    const merged = DEFAULT_SENSORS.map(def => {
                        const found = parsed.find((p: SensorConfig) => p.key === def.key)
                        return found ? { ...def, visible: found.visible } : def
                    })
                    setConfigs(merged)
                }
            } catch (e) {
                console.error('Failed to parse sensor configs', e)
            }
        }
    }, [])

    const toggleSensor = (key: string) => {
        setConfigs(prev => prev.map(c =>
            c.key === key ? { ...c, visible: !c.visible } : c
        ))
    }

    const handleSave = () => {
        localStorage.setItem('sensor_configs', JSON.stringify(configs))
        onSave(configs)
        onClose()
    }

    if (!isOpen) return null

    const categories = ['water', 'system', 'location', 'imu'] as const

    const getCategoryLabel = (cat: string) => {
        switch (cat) {
            case 'water': return t('settings.water')
            case 'system': return t('settings.system')
            case 'location': return t('dashboard.navigation')
            case 'imu': return t('dashboard.navigation')
            default: return cat
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="glass-dark rounded-xl w-full max-w-[600px] max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between flex-none bg-white/5">
                    <div className="flex flex-col">
                        <h3 className="text-lg font-semibold text-white">{t('settings.title')}</h3>
                        <p className="text-xs text-white/40">{t('settings.subtitle')}</p>
                    </div>
                    <button onClick={onClose} className="text-white/60 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-6 min-h-0">
                    {categories.map(cat => (
                        <div key={cat}>
                            <h4 className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3 sticky top-0 bg-slate-900/90 backdrop-blur-sm py-1 z-10">
                                {getCategoryLabel(cat)}
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {configs.filter(c => c.category === cat).map(sensor => (
                                    <button
                                        key={sensor.key}
                                        onClick={() => toggleSensor(sensor.key)}
                                        className={`flex items-center justify-between p-3 rounded-lg transition-all border ${sensor.visible
                                            ? 'bg-primary-500/20 border-primary-500/50 hover:bg-primary-500/30'
                                            : 'bg-white/5 border-white/5 hover:bg-white/10'
                                            }`}
                                    >
                                        <span className={`text-sm font-medium ${sensor.visible ? 'text-white' : 'text-white/60'
                                            }`}>
                                            {t(`sensors.${sensor.key}`)}
                                        </span>
                                        {sensor.visible ? (
                                            <Eye className="w-4 h-4 text-primary-400" />
                                        ) : (
                                            <EyeOff className="w-4 h-4 text-white/40" />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-white/10 flex justify-end gap-3 flex-none bg-black/20 backdrop-blur-md">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-all"
                    >
                        {t('settings.cancel')}
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium flex items-center gap-2 transition-all shadow-lg shadow-primary-600/20"
                    >
                        <Save className="w-4 h-4" />
                        {t('settings.save')}
                    </button>
                </div>
            </div>
        </div>
    )
}
